/**
 * Shared resolver for Storyblok link fields (`link_url`, `button_link`,
 * `image_link`, menu links, …). Thin reactive wrapper around the pure
 * helpers in `utils/storyblok/storyblokLinkPath.ts` — see that file (and its
 * tests) for the stripping rules.
 */

import {
	stripStoryblokSitePrefixes,
	storyblokLinkPath,
	resolveStoryblokHref,
	type StoryblokLinkField
} from "~/utils/storyblok/storyblokLinkPath";

export type { StoryblokLinkField };

export const useStoryblokLink = () => {
	const { folder, fallbackFolder } = useStoryblokSiteFolder();
	const storyblokLanguage = useStoryblokLanguage();
	const localePath = useLocalePath();
	const { locales } = useI18n();
	const { createCategoryLink } = useCategory();

	const context = () => ({
		folder: folder.value,
		fallbackFolder: fallbackFolder.value,
		// Always a locale code or ISO string at runtime; typed `{}` only because
		// it is derived from untyped runtime config.
		language: storyblokLanguage.value as string | undefined
	});

	const stripSitePrefixes = (slug: string): string =>
		stripStoryblokSitePrefixes(slug, context());

	const linkPath = (link?: StoryblokLinkField | null): string =>
		storyblokLinkPath(link, context());

	// Memoized: re-mapping the locale list on every resolve adds up across the
	// many link renderers on a page.
	const localeCodes = computed(() =>
		locales.value.map((locale) =>
			typeof locale === "string" ? locale : locale.code
		)
	);

	/**
	 * The one resolver for Storyblok link fields: localizes exactly once for the
	 * active market, never touches external URLs, and falls back to the Norce
	 * category `id`. Returns `""` when nothing resolves, so callers add their own
	 * localized fallback (`|| localePath("/")`). Rules and tests:
	 * `resolveStoryblokHref` in `utils/storyblok/storyblokLinkPath.ts`.
	 *
	 * Call it during setup or render, not inside a lazily-evaluated computed: the
	 * category fallback reads the i18n locale and the category store.
	 */
	const resolveLink = (
		link?: StoryblokLinkField | string | null,
		categoryId?: string | number | null
	): string =>
		resolveStoryblokHref(link, categoryId, context(), {
			localePath,
			resolveCategory: (id) =>
				createCategoryLink({ category: { CategoryId: Number(id) } }),
			localeCodes: localeCodes.value
		});

	/**
	 * Href for a pre-resolved menu node (`ResolvedMenuNode` from norce-pwa).
	 * The package builds story hrefs from `full_slug`, so the site folder is
	 * stripped here at render time; external/anchor nodes pass through.
	 */
	const resolveMenuHref = (node: { type?: string; href: string }): string => {
		if (node.type === "external" || node.type === "anchor") return node.href;
		return localePath(`/${stripSitePrefixes(node.href)}`);
	};

	return { linkPath, stripSitePrefixes, resolveLink, resolveMenuHref };
};
